import { HttpClient } from '@angular/common/http';
import {
  Injectable,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  Observable,
  catchError,
  finalize,
  of,
  shareReplay,
  tap,
} from 'rxjs';

import {
  RuntimeConfig,
  RuntimeModule,
} from './runtime-config.model';

const DEFAULT_RUNTIME: RuntimeConfig = {
  regional: {
    locale: 'pt-BR',
    fusoHorario: 'America/Sao_Paulo',
    formatoData: 'DD/MM/YYYY',
    formatoHora: '24H',
    versao: 0,
  },
  modulos: [
    {
      codigo: 'PROCESSOS',
      nome: 'Processos',
      descricao: 'Gestão da carteira jurídica.',
      habilitado: true,
      disponivelProduto: true,
      essencial: true,
      ordem: 10,
      versao: 0,
    },
    {
      codigo: 'AGENDA',
      nome: 'Agenda',
      descricao: null,
      habilitado: false,
      disponivelProduto: true,
      essencial: false,
      ordem: 20,
      versao: 0,
    },
    {
      codigo: 'MEDIACAO',
      nome: 'Mediação e Conciliação',
      descricao: 'Procedimentos consensuais, mediadores, conciliadores, sessões, propostas e acordos.',
      habilitado: true,
      disponivelProduto: true,
      essencial: false,
      ordem: 27,
      versao: 0,
    },
    {
      codigo: 'WORKFLOW',
      nome: 'Workflow jurídico',
      descricao: 'Fluxos, etapas, responsáveis, SLAs e automações da operação jurídica.',
      habilitado: true,
      disponivelProduto: true,
      essencial: false,
      ordem: 25,
      versao: 0,
    },
    {
      codigo: 'DOCUMENTOS',
      nome: 'Documentos',
      descricao: 'Peças, anexos e versões vinculados aos processos.',
      habilitado: false,
      disponivelProduto: true,
      essencial: false,
      ordem: 30,
      versao: 0,
    },
    {
      codigo: 'COMUNICACOES',
      nome: 'Comunicações',
      descricao: 'Publicações, intimações e comunicações processuais.',
      habilitado: true,
      disponivelProduto: true,
      essencial: false,
      ordem: 35,
      versao: 0,
    },
    {
      codigo: 'FINANCEIRO',
      nome: 'Financeiro jurídico',
      descricao: 'Eventos financeiros vinculados aos processos.',
      habilitado: true,
      disponivelProduto: true,
      essencial: false,
      ordem: 38,
      versao: 0,
    },
    {
      codigo: 'RISCO_PROVISIONAMENTO',
      nome: 'Risco e provisionamento',
      descricao: 'Prognóstico de risco, provisões e validação por alçada.',
      habilitado: true,
      disponivelProduto: true,
      essencial: false,
      ordem: 39,
      versao: 0,
    },
    {
      codigo: 'RELATORIOS',
      nome: 'Relatórios',
      descricao: null,
      habilitado: false,
      disponivelProduto: false,
      essencial: false,
      ordem: 40,
      versao: 0,
    },
  ],
  features: [
    {
      codigo: 'DASHBOARD_RESUMO_EXECUTIVO',
      nome: 'Resumo executivo da visão geral',
      categoria: 'DASHBOARD',
      habilitada: true,
    },
    {
      codigo: 'DASHBOARD_ATALHOS',
      nome: 'Atalhos da Visão Geral',
      categoria: 'DASHBOARD',
      habilitada: true,
    },
    {
      codigo: 'DASHBOARD_RESUMO_PROCESSOS',
      nome: 'Painel de processos',
      categoria: 'DASHBOARD',
      habilitada: true,
    },
    {
      codigo: 'DASHBOARD_RESUMO_AGENDA',
      nome: 'Painel de Agenda',
      categoria: 'DASHBOARD',
      habilitada: true,
    },
    {
      codigo: 'DASHBOARD_COMUNICACOES',
      nome: 'Painel de comunicações',
      categoria: 'DASHBOARD',
      habilitada: true,
    },
    {
      codigo: 'DASHBOARD_MONITORAMENTO',
      nome: 'Painel de monitoramento processual',
      categoria: 'DASHBOARD',
      habilitada: true,
    },
    {
      codigo: 'DASHBOARD_CONSULTIVO_WORKFLOW',
      nome: 'Painel de Consultivo e workflows',
      categoria: 'DASHBOARD',
      habilitada: true,
    },
    {
      codigo: 'DASHBOARD_FINANCEIRO_RISCO',
      nome: 'Painel financeiro e de risco',
      categoria: 'DASHBOARD',
      habilitada: true,
    },
    {
      codigo: 'DASHBOARD_TERCEIRIZACAO',
      nome: 'Painel de terceirização jurídica',
      categoria: 'DASHBOARD',
      habilitada: true,
    },
  ],
};

@Injectable({ providedIn: 'root' })
export class RuntimeConfigService {
  private readonly http = inject(HttpClient);

  private readonly state =
    signal<RuntimeConfig>(DEFAULT_RUNTIME);

  private loaded = false;
  private inFlight: Observable<RuntimeConfig> | null = null;

  readonly config = this.state.asReadonly();
  readonly regional = computed(() => this.state().regional);

  readonly enabledModules = computed(
    () =>
      this.state().modulos
        .filter(
          (module) =>
            module.habilitado
            && module.disponivelProduto,
        )
        .sort((a, b) => a.ordem - b.ordem),
  );

  load(force = false): void {
    this.ensureLoaded(force).subscribe();
  }

  ensureLoaded(force = false): Observable<RuntimeConfig> {
    if (this.loaded && !force) {
      return of(this.state());
    }

    if (this.inFlight && !force) {
      return this.inFlight;
    }

    const request = this.http
      .get<RuntimeConfig>('/api/public/runtime')
      .pipe(
        catchError(() => of(DEFAULT_RUNTIME)),
        tap((runtime) => {
          this.state.set(runtime);
          this.loaded = true;
        }),
        finalize(() => {
          this.inFlight = null;
        }),
        shareReplay(1),
      );

    this.inFlight = request;
    return request;
  }

  module(code: string): RuntimeModule | null {
    return this.state().modulos
      .find((module) => module.codigo === code)
      ?? null;
  }

  isModuleEnabled(code: string): boolean {
    const module = this.module(code);

    return Boolean(
      module?.habilitado
      && module.disponivelProduto,
    );
  }

  isFeatureEnabled(code: string): boolean {
    return this.state().features
      .find((feature) => feature.codigo === code)
      ?.habilitada
      ?? false;
  }
}
